import React, { useRef, useCallback, useEffect, createContext } from 'react';

// Context read by Section.js: any Section rendered inside a SectionModal
// starts expanded. Everywhere else (default false) keeps the normal dropdown.
export const SectionOpenContext = createContext(false);

// Magic Bento style card primitives (reactbits-inspired, no GSAP dependency).
// Effects: border glow that follows the cursor, inner spotlight, click ripple, hover lift.
// Styles live in ../styles/ViewOrderBento.css

export function BentoGrid({ children }) {
  return <div className="bento-grid">{children}</div>;
}

export function BentoCard({ label, title, onClick, span = 1, tall = false, className = '', children }) {
  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    card.style.setProperty('--glow-x', `${x}%`);
    card.style.setProperty('--glow-y', `${y}%`);
  }, []);

  const handleMouseEnter = useCallback(() => {
    cardRef.current?.style.setProperty('--glow-intensity', '1');
  }, []);

  const handleMouseLeave = useCallback(() => {
    cardRef.current?.style.setProperty('--glow-intensity', '0');
  }, []);

  const handleClick = useCallback((e) => {
    const card = cardRef.current;
    if (card && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const rect = card.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.className = 'bento-ripple';
      ripple.style.left = `${e.clientX - rect.left}px`;
      ripple.style.top = `${e.clientY - rect.top}px`;
      card.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove());
    }
    if (onClick) onClick();
  }, [onClick]);

  return (
    <div
      ref={cardRef}
      className={`bento-card bento-span-${span} ${tall ? 'bento-tall' : ''} ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (onClick) onClick();
        }
      }}
    >
      <div className="bento-card-header">
        <span className="bento-card-label">{label}</span>
        <span className="bento-card-open" aria-hidden="true">Open →</span>
      </div>
      {title && <h3 className="bento-card-title">{title}</h3>}
      <div className="bento-card-body">{children}</div>
    </div>
  );
}

export function SectionModal({ isOpen, title, onClose, children }) {
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="bento-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bento-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="bento-modal-header">
          <h2>{title}</h2>
          <button type="button" className="bento-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="bento-modal-body" ref={bodyRef}>
          <SectionOpenContext.Provider value={true}>
            {children}
          </SectionOpenContext.Provider>
        </div>
      </div>
    </div>
  );
}